/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Remove the above eslint-disable when game actions (battle, tournaments, challenges, pending games) are re-enabled
import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  StakeFiatCurrency,
  TournamentEntryAllOfStakeOptionId,
} from "@nestcoinco/hyper-api-gateway-api-client";
import ChevronLeftCircleIcon from "../../components/icons/ChevronLeftCircleIcon";
import { Button, Image, Stack } from "react-bootstrap";
import ImageFriendsAvatar from "../../images/friends-avatar.svg";
import "./PlayGame.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import InfoIcon from "../../components/icons/InfoIcon";
import { formatCurrency } from "../../utils/helpers";
import DownloadAppPrompt from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../hooks/useDisclosure";
import HyperCoin from "../../components/HyperCoin/HyperCoin";
import Gem from "../../components/Gem/Gem";
import GemInfoModal from "../../components/dashboard-page/modals/GemInfoModal/GemInfoModal";
import BottomDrawer from "../../components/BottomDrawer/BottomDrawer";
import { GenericModalProps } from "../../utils/constants/generic-types";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import CustomPopover from "../../components/CustomPopover/CustomPopover";
import {
  getStoredHasViewedFirstGameExplainer,
  setStoredHasViewedFirstGameExplainer,
} from "../../utils/storage";
import LeaderboardTab from "../../components/play-game-page/LeaderboardTab/LeaderboardTab";
import BarChartIcon from "../../components/icons/BarChartIcon";
import GameControllerIcon from "../../components/icons/GameControllerIcon";
import { ROUTES, usePlayGameMetaDescription } from "../../utils/constants";
import AgeRestrictionModal from "../../components/AgeRestrictionModal/AgeRestrictionModal";
import useStakeOptionsQuery from "../../react-query/queries/use-stake-options";
import useDuelEntryMutation from "../../react-query/mutations/use-duel-entry";
import { useAtom } from "jotai";
import useBalancesQuery from "../../react-query/queries/use-balances";
import useRecentGamersSummaryQuery from "../../react-query/queries/use-recent-gamers-summary";
import usePlayPracticeGameMutation from "../../react-query/mutations/use-play-practice-game";
import TopUpToPlayModal from "../../components/modals/TopUpToPlay/TopUpToPlayModal";
import DownloadToPlayModal from "../../components/modals/DownloadToPlay/DownloadToPlayModal";
import PlayInAppModal from "../../components/modals/PlayInApp/PlayInAppModal";
import useAuth from "../../hooks/useAuth";
import { didUserClickToInitiateGameAtom } from "../../jotai/atoms/games";
import { CustomSpinner } from "../../components/CustomSpinner/CustomSpinner";
import useGameDetailsBySlugQuery from "../../react-query/queries/use-game-details-by-slug";
import { PROMPT_DOWNLOAD_SCENARIOS } from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import {
  handleGetOnboardingState,
  handleUpdateOnboardingState,
} from "../../api-clients/userAccountClient";
import HyperBalance, {
  renderBalanceContent,
} from "../../components/dashboard-page/HyperBalance/HyperBalance";
import AvatarGroup from "../../components/AvatarGroup/AvatarGroup";
import useTopupFlow from "../../hooks/useTopupFlow";
import TopupFlow from "../../components/topup/TopupFlow/TopupFlow";
import { extractOgTitleFromMainTitle, renderHeaders } from "../../utils/renderHeaders";
import { openReportIssue } from "../../utils/report-issue-url";
import { cx } from "../../utils/cx";
import Skeleton from "../../components/Skeleton/Skeleton";
import ArrowRightIcon from "../../components/icons/ArrowRightIcon";
import useUserVerificationStatusQuery from "../../react-query/queries/use-user-verification-status";
import useHprFiatFormatter from "../../hooks/useHprFiatFormatter";
import useGetApp from "../../hooks/useGetApp";

interface GameInfoProps extends GenericModalProps {
  title: string;
  description: React.ReactNode;
}

interface GameProps {
  title: string;
  isMultiplayer?: boolean;
  noOfPlayers?: number;
  avatarUrls?: string[];
  isFiat?: boolean;
}

interface GameStakeProps {
  reward: number;
  stakeAmount: number;
  handlePlayGame?: () => void;
  isLoading?: boolean;
  selectedStakeId?: number;
  isFiat?: boolean;
}

function LoadingSkeleton() {
  return (
    <div>
      <Skeleton height={300} />
      <div className="p-3">
        <Skeleton height={40} className="mb-4" />
        <Skeleton height={74} className="mb-4" />
        {new Array(3).fill("_").map((_, i) => (
          <Fragment key={i}>
            <Skeleton height={24} width={64} className="mb-12" />
            <Skeleton height={100} className="mb-4" />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function GameInfo({ isOpen, onClose, title, description }: GameInfoProps) {
  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose}>
      <Stack className="align-items-center GameInfo">
        <InfoIcon width={105} height={105} />
        <Stack gap={4}>
          <Stack gap={2} className="align-items-center">
            <h2 className="fs-6 Hanson-700 SpecialFontSettings GameTitle">
              {title}
            </h2>
            <p className="text-center Grotesk SpecialFontSettings GameDescription">
              {description}
            </p>
          </Stack>
          <PrimaryActionButton size="lg" text="Close" onClick={onClose} />
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}

function Game({
  title,
  isMultiplayer,
  noOfPlayers,
  avatarUrls,
  isFiat,
  children,
}: PropsWithChildren<GameProps>) {
  const {
    isOpen: isGameInfoOpen,
    onOpen: openGameInfo,
    onClose: closeGameInfo,
  } = useDisclosure();
  const { t } = useTranslation("games");
  const gameInfoDescription = {
    practice: t("master_games_via_practice_mode_and_compete_key"),
    battle: isFiat ? (
      <span>{t("battle_mode_explainer_content_fiat")}</span>
    ) : (
      <span className="">
        {t("play_against_real_gamers_in_your_skill_level_to_win")}{" "}
        <HyperCoin className="HyperCoin" />{" "}
        {t("hpr_rewards_cash_out_your_hpr_for_real_money_key")}
      </span>
    ),
  };

  let leftOffset: number;
  if (!avatarUrls?.length) leftOffset = 0;
  else if (avatarUrls.length === 1) leftOffset = 4;
  else if (avatarUrls.length === 2) leftOffset = -8;
  else leftOffset = -16;

  return (
    <>
      <Stack className="Game">
        <div className="d-flex justify-content-between">
          <h2 className="Title">{title}</h2>
          <button type="button" className="p-0 InfoBtn" onClick={openGameInfo}>
            <InfoIcon />
          </button>
        </div>
        {isMultiplayer && !!noOfPlayers && (
          <div className="align-self-start d-flex align-items-center">
            <AvatarGroup avatars={avatarUrls} />
            <p
              className="Grotesk SpecialFontSettings NoOfPlayers position-relative"
              style={{ left: leftOffset }}
            >
              <span>{noOfPlayers}</span>{" "}
              <span className="fw-bold">{t("playing")}</span>
            </p>
          </div>
        )}
        {children}
      </Stack>
      <GameInfo
        isOpen={isGameInfoOpen}
        onClose={closeGameInfo}
        title={isMultiplayer ? t("battle_game") : t("practice_game")}
        description={
          isMultiplayer
            ? gameInfoDescription.battle
            : gameInfoDescription.practice
        }
      />
    </>
  );
}

function GameStake({
  reward,
  stakeAmount,
  handlePlayGame,
  isLoading,
  isFiat,
}: GameStakeProps) {
  const [isSelected, setIsSelected] = useState(false);
  const { t } = useTranslation("games");
  const { formatHprToFiatValue } = useHprFiatFormatter();

  const formattedReward = isFiat
    ? formatHprToFiatValue(reward)
    : formatCurrency(reward);
  const formattedStake = isFiat
    ? formatHprToFiatValue(stakeAmount)
    : formatCurrency(stakeAmount);

  return (
    <Stack gap={2} className="GameStake">
      <Stack direction="horizontal" gap={2}>
        <Stack direction="horizontal" gap={1} className="align-items-center">
          {!isFiat && <HyperCoin width={23} height={23} />}
          <p className="RewardAmount">{formattedReward}</p>
        </Stack>
        <p className="RewardText">{t("reward")}</p>
      </Stack>
      <PrimaryActionButton
        size="lg"
        text={
          isLoading && isSelected ? (
            <CustomSpinner size="sm" />
          ) : stakeAmount === 0 ? (
            t("play_for_free")
          ) : (
            <span className="d-inline-flex align-items-center" style={{ gap: 2 }}>
              {t("play_with")} {!isFiat && <HyperCoin width={12.5} height={12.5} />}
              <span>{formattedStake}</span>
            </span>
          )
        }
        onClick={() => {
          setIsSelected(true);
          handlePlayGame && handlePlayGame();
        }}
        disabled={isLoading}
      />
    </Stack>
  );
}

const LeaderboardExplainerContent = ({ isFiat }: { isFiat: boolean }) => {
  const { t } = useTranslation("games");

  if (isFiat) {
    return <span>{t("leaderboard_explainer_content_fiat")}</span>;
  }

  return (
    <span>
      {t("leaderboard_explainer_content.start")} <HyperCoin height={16} />
      {t("leaderboard_explainer_content.end")}
    </span>
  );
};

const ChallengeFriendsExplainerContent = ({ isFiat }: { isFiat: boolean }) => {
  const { t } = useTranslation("games");

  return (
    <span>
      {t("challenge_friends_explainer_content")}{" "}
      {!isFiat && <HyperCoin height={16} />}
    </span>
  );
};

const BattleModeExplainerContent = ({ isFiat }: { isFiat: boolean }) => {
  const { t } = useTranslation("games");

  if (isFiat) {
    return <span>{t("battle_mode_explainer_content_fiat")}</span>;
  }

  return (
    <span>
      {t("battle_mode_explainer_content_hpr.start")} <HyperCoin height={16} />{" "}
      {t("battle_mode_explainer_content_hpr.end")}
    </span>
  );
};

enum FirstGameExplainers {
  PLAY_WITH_FRIENDS,
  LEADERBOARD,
  PRACTICE_MODE,
  BATTLE_MODE,
}
const EXPLAINER_STEP_COUNT = Object.values(FirstGameExplainers).filter(
  (value) => typeof value === "string"
).length;

function PlayGame() {
  const navigate = useNavigate();
  const { isLoggedIn, userProfile } = useAuth();
  const { gameSlug } = useParams();
  const [tabIndex, setTabIndex] = useState<0 | 1>(0);
  const { isLegalAge } = useUserVerificationStatusQuery();
  const {
    modalsProps,
    ipLocationCountry,
    handleTopup,
    handleGetDeviceLocationInfo,
  } = useTopupFlow();
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isAgeRestrictionOpen,
    onOpen: openAgeRestriction,
    onClose: closeAgeRestriction,
  } = useDisclosure();
  const {
    isOpen: isGemInfoModalOpen,
    onOpen: openGemInfoModal,
    onClose: closeGemInfoModal,
  } = useDisclosure();
  const {
    isOpen: isTopUpModalOpen,
    onOpen: openTopUpModal,
    onClose: closeTopUpModal,
  } = useDisclosure();
  const {
    isOpen: isDownloadToPlayOpen,
    onOpen: openDownloadToPlay,
    onClose: closeDownloadToPlay,
  } = useDisclosure();
  const {
    isOpen: isPlayInAppOpen,
    onOpen: openPlayInApp,
    onClose: closePlayInApp,
  } = useDisclosure();
  const { appExists } = useGetApp();
  const [activeExplainer, setActiveExplainer] = useState<number>();
  const [, setDidUserClickToInitiateGame] = useAtom(
    didUserClickToInitiateGameAtom
  );
  const { data: stakeOptions, isLoading: isStakeOptionsLoading } =
    useStakeOptionsQuery({
      currency: userProfile?.currency as StakeFiatCurrency,
    });
  const {
    playBattleGame,
    mutation: { isPending: isDuelEntryPending },
  } = useDuelEntryMutation();
  const { data: gameDetails, isLoading: isGameDetailsLoading } =
    useGameDetailsBySlugQuery({ slug: gameSlug ?? "" });
  const gameId = gameDetails?.id;
  const gameName = gameDetails?.name;
  const { data: recentGamersSummary } = useRecentGamersSummaryQuery(gameId);
  const {
    hprBalance,
    gemBalance,
    isLoading: isBalanceLoading,
  } = useBalancesQuery();
  const {
    playPracticeGame,
    mutation: { isPending: isPracticeGameEntryPending },
  } = usePlayPracticeGameMutation({ gameId: gameId || "" });
  const { t } = useTranslation(["games"]);
  const metaDescriptions = usePlayGameMetaDescription();
  const { isFiatDisplay } = useHprFiatFormatter();

  const isLeaderboardExplainerOpen =
    activeExplainer === FirstGameExplainers.LEADERBOARD;
  const isPlayWithFriendsExplainerOpen =
    activeExplainer === FirstGameExplainers.PLAY_WITH_FRIENDS;
  const isPracticeModeExplainerOpen =
    activeExplainer === FirstGameExplainers.PRACTICE_MODE;
  const isBattleModeExplainerOpen =
    activeExplainer === FirstGameExplainers.BATTLE_MODE;

  const onFlowEnd = useCallback(() => {
    setStoredHasViewedFirstGameExplainer(true);
    isLoggedIn && handleUpdateOnboardingState({ gameTourGuide: true });
  }, [isLoggedIn]);

  useEffect(() => {
    if (getStoredHasViewedFirstGameExplainer()) return;

    const handleExplainer = async () => {
      const state = isLoggedIn ? await handleGetOnboardingState() : null;
      if (!state?.gameTourGuide && isLoggedIn) setActiveExplainer(0);
      if (state?.gameTourGuide) setStoredHasViewedFirstGameExplainer(true);
    };

    handleExplainer();
  }, [isLoggedIn]);

  const viewLeaderboard = () => {
    if (!isLoggedIn) {
      openDownloadAppPrompt();
      return;
    }
    setTabIndex(1);
  };

  const initiateBattleGame = (
    stakeOptionId: TournamentEntryAllOfStakeOptionId,
    gameId: string
  ) => {
    if (!isLoggedIn) {
      openDownloadAppPrompt();
      return;
    }

    const stakeAmount = stakeOptions?.records.find(
      (option) => option.id === stakeOptionId
    )?.stakeAmount;

    if (hprBalance === undefined || stakeAmount === undefined) return;
    if (hprBalance < stakeAmount) {
      openTopUpModal();
      return;
    }

    if (isLegalAge === undefined) return;
    if (!isLegalAge) {
      openAgeRestriction();
      return;
    }

    if (!appExists) {
      openDownloadToPlay();
      return;
    }

    openPlayInApp();
  };

  const initiatePracticeGame = () => {
    playPracticeGame();
  };

  const openReportTrouble = () => {
    openReportIssue({
      gameName: gameDetails?.name,
      userId: userProfile?.id,
      username: userProfile?.username,
    });
  };

  useLayoutEffect(() => {
    setDidUserClickToInitiateGame(false);
  }, [setDidUserClickToInitiateGame]);

  if (isStakeOptionsLoading || isGameDetailsLoading) return <LoadingSkeleton />;

  return (
    <>
      {renderHeaders({
        title: t("metadata:game_name_play_now_hyper_gaming_app", { gameName }),
        description: metaDescriptions.generatePlayGameDescription(gameName),
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:game_name_play_now_hyper_gaming_app", { gameName })
        ),
        image: gameDetails?.poster,
      })}

      <div className="MobileOnly PlayGamePage">
        <div
          className="PlayGameBanner"
          style={{
            backgroundImage: `linear-gradient(185deg, #000 3.71%, rgba(0, 0, 0, 0.00) 96.28%), url(${gameDetails?.poster})`,
          }}
        >
          <div className="PlayGameHeader position-relative">
            <button
              type="button"
              onClick={() =>
                navigate(`${ROUTES.GAMES.ROOT}/${gameDetails?.slug}`)
              }
              className="position-relative BackBtn"
              style={{ zIndex: 10 }}
            >
              <ChevronLeftCircleIcon />
            </button>
            <h1
              className="GameTitle"
              style={{ position: isLoggedIn ? "absolute" : "static" }}
            >
              {gameDetails?.name}
            </h1>
            {!isLoggedIn && (
              <Link
                to={ROUTES.ROOT.LOG_IN}
                className="text-uppercase text-decoration-none Grotesk LoginCta"
              >
                {t("login")}
              </Link>
            )}
          </div>
          <Stack direction="horizontal" gap={isLoggedIn ? 3 : 2}>
            <HyperBalance balance={hprBalance} isLoading={isBalanceLoading} />
            <button
              className="GemBalance"
              onClick={openGemInfoModal}
              type="button"
            >
              <Gem width={20} height={17.8} />
              <div>
                {renderBalanceContent({
                  isLoading: isBalanceLoading,
                  balance: gemBalance,
                })}
              </div>
            </button>
          </Stack>
        </div>

        <Stack gap={4} className="py-4 px-3 Tabs">
          <Stack direction="horizontal" gap={4} className="p-1 TabList">
            <Button
              className={cx("Tab", tabIndex === 0 && "active")}
              onClick={() => setTabIndex(0)}
            >
              <GameControllerIcon
                fill={tabIndex === 0 ? "white" : "#8c8c8c"}
                width={16}
                aria-label="game controller"
              />
              {t("play")}
            </Button>
            <CustomPopover
              explainerSteps={EXPLAINER_STEP_COUNT}
              isOpen={isLeaderboardExplainerOpen}
              setActiveExplainer={setActiveExplainer}
              popoverContent={
                <LeaderboardExplainerContent isFiat={isFiatDisplay} />
              }
              heading="Leaderboard"
              activeExplainer={activeExplainer}
              childrenContainerStyles={{ flex: "1 1 45%" }}
            >
              <span
                className={`${isLeaderboardExplainerOpen && "popover-node"}`}
                style={{
                  border: isLeaderboardExplainerOpen ? undefined : "none",
                  padding: isLeaderboardExplainerOpen ? "4px" : 0,
                  borderRadius: 13,
                  width: "100%",
                  display: "inline-block",
                }}
              >
                <Button
                  className={cx("Tab", tabIndex === 1 && "active")}
                  onClick={viewLeaderboard}
                >
                  <BarChartIcon
                    fill={tabIndex === 1 ? "white" : "#8c8c8c"}
                    aria-label="bar chart icon"
                  />
                  {t("leaderboard")}
                </Button>
              </span>
            </CustomPopover>
          </Stack>
          <div className="TabPanels">
            {tabIndex === 0 && (
              <>
              <Stack gap={4} className="TabPanel">
                <CustomPopover
                  explainerSteps={EXPLAINER_STEP_COUNT}
                  isOpen={isPlayWithFriendsExplainerOpen}
                  setActiveExplainer={setActiveExplainer}
                  popoverContent={
                    <ChallengeFriendsExplainerContent isFiat={isFiatDisplay} />
                  }
                  heading="Challenge friends"
                  activeExplainer={activeExplainer}
                >
                  <div
                    className={`${
                      isPlayWithFriendsExplainerOpen ? "popover-node" : ""
                    } fluid-popover-node`}
                    style={{
                      border: isPlayWithFriendsExplainerOpen
                        ? undefined
                        : "none",
                      padding: isPlayWithFriendsExplainerOpen ? 4 : 0,
                    }}
                  >
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="justify-content-between ChallengeFriends"
                    >
                      <Stack direction="horizontal" gap={2} className="w-100">
                        <Image
                          src={ImageFriendsAvatar}
                          alt=""
                          width={52}
                          height={42}
                        />
                        <Stack
                          as="button"
                          gap={1}
                          className="d-block ChallengeFriendsCta"
                          onClick={openDownloadAppPrompt}
                        >
                          <p className="text-start CardTitle">
                            {t("common:play_with_friends")}
                          </p>
                          <p className="text-start CardDescription">
                            {t(
                              "create_a_custom_game_link_and_share_with_friends"
                            )}
                          </p>
                        </Stack>
                        <ArrowRightIcon style={{ fill: "white" }} />
                      </Stack>
                    </Stack>
                  </div>
                </CustomPopover>
                <CustomPopover
                  explainerSteps={EXPLAINER_STEP_COUNT}
                  isOpen={isPracticeModeExplainerOpen}
                  setActiveExplainer={setActiveExplainer}
                  popoverContent={
                    isFiatDisplay
                      ? t("master_games_via_practice_mode_key_fiat")
                      : t("master_games_via_practice_mode_key")
                  }
                  heading={t("practice_mode")}
                  activeExplainer={activeExplainer}
                >
                  <div
                    className={`${
                      isPracticeModeExplainerOpen ? "popover-node" : ""
                    } fluid-popover-node`}
                    style={{
                      border: isPracticeModeExplainerOpen ? undefined : "none",
                      padding: isPracticeModeExplainerOpen ? 6 : 0,
                    }}
                  >
                    <Game title={t("practice")} isFiat={isFiatDisplay}>
                      <GameStake
                        reward={0}
                        stakeAmount={0}
                        handlePlayGame={initiatePracticeGame}
                        isLoading={
                          isPracticeGameEntryPending || isDuelEntryPending
                        }
                        isFiat={isFiatDisplay}
                      />
                    </Game>
                  </div>
                </CustomPopover>
                <CustomPopover
                  explainerSteps={EXPLAINER_STEP_COUNT}
                  isOpen={isBattleModeExplainerOpen}
                  setActiveExplainer={setActiveExplainer}
                  popoverContent={
                    <BattleModeExplainerContent isFiat={isFiatDisplay} />
                  }
                  heading={t("battle_mode")}
                  activeExplainer={activeExplainer}
                  positions="top"
                  onFlowEnd={onFlowEnd}
                >
                  <div
                    className={`${
                      isBattleModeExplainerOpen ? "popover-node" : ""
                    } fluid-popover-node`}
                    style={{
                      border: isBattleModeExplainerOpen ? undefined : "none",
                      padding: isBattleModeExplainerOpen ? 6 : 0,
                    }}
                  >
                    <Game
                      title={t("battle")}
                      isMultiplayer
                      noOfPlayers={recentGamersSummary?.count}
                      avatarUrls={recentGamersSummary?.avatarUrls}
                      isFiat={isFiatDisplay}
                    >
                      {stakeOptions?.records.map(
                        ({ id, rewardAmount, stakeAmount }) => (
                          <GameStake
                            key={id}
                            reward={rewardAmount}
                            stakeAmount={stakeAmount}
                            handlePlayGame={() =>
                              gameId && initiateBattleGame(id, gameId)
                            }
                            isLoading={
                              isDuelEntryPending || isPracticeGameEntryPending
                            }
                            isFiat={isFiatDisplay}
                          />
                        )
                      )}
                    </Game>
                  </div>
                </CustomPopover>
              </Stack>
              <p className="ReportTrouble">
                <Trans
                  t={t}
                  i18nKey="having_trouble_playing_this_game_report"
                  components={{
                    report: (
                      <button
                        type="button"
                        className="ReportLink"
                        onClick={openReportTrouble}
                      />
                    ),
                  }}
                />
              </p>
              </>
            )}
            {tabIndex === 1 && <LeaderboardTab />}
          </div>
        </Stack>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={
          isLoggedIn
            ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_TOURNAMENT
            : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_TOURNAMENT
        }
      />
      <AgeRestrictionModal
        isOpen={isAgeRestrictionOpen}
        onClose={closeAgeRestriction}
        toastMessage={t("toasts:ineligible_to_play_battle_games")}
      />
      <GemInfoModal
        show={isGemInfoModalOpen}
        onHide={closeGemInfoModal}
        balance={`${gemBalance || ""}`}
      />
      <TopUpToPlayModal
        isOpen={isTopUpModalOpen}
        onClose={closeTopUpModal}
        handleTopup={handleTopup}
      />
      <DownloadToPlayModal
        isOpen={isDownloadToPlayOpen}
        onClose={closeDownloadToPlay}
      />
      <PlayInAppModal
        isOpen={isPlayInAppOpen}
        onClose={closePlayInApp}
        gameName={gameDetails?.name}
        thumbnail={gameDetails?.thumbnail}
      />
      <TopupFlow
        {...modalsProps}
        handleTopup={handleTopup}
        ipLocationCountry={ipLocationCountry}
        handleGetDeviceLocationInfo={handleGetDeviceLocationInfo}
      />
    </>
  );
}

export default PlayGame;
